import { useRef, useEffect } from 'react';

import { Box, Typography } from '@mui/material';
import { J } from './contentPanelTheme';
import EntryRow from './EntryRow';
import { useInspector } from '../../contexts/InspectorContext';
import useKeyboardNav from '../../hooks/useKeyboardNav';
import _ from 'lodash';

// Pure list renderer — search is handled by EntryPanelArea
export default function EntryPanel({ entries, selectedEntry, onSelect }) {
    const { isDirty } = useInspector();
    const listRef = useRef(null);

    // Scroll selected entry into view — find the real scroll parent via computed
    // style, niet via inline style attribuut. MUI's sx overflowY compileert naar
    // een emotion class, niet naar een inline "style" attribuut, dus closest('[style*="overflow"]')
    // pakte in YourDataTab.jsx de buitenste react-resizable-panels <Panel> (clipt alleen,
    // scrollt zelf niet) i.p.v. de echte #entry-panel-scroll-area div.
    const getScrollParent = (el) => {
        let node = el.parentElement;
        while (node) {
            const { overflowY } = getComputedStyle(node);
            if (overflowY === 'auto' || overflowY === 'scroll') return node;
            node = node.parentElement;
        }
        return null;
    };

    useEffect(() => {
        if (!selectedEntry || !listRef.current) return;

        setTimeout(() => {
            const selected = listRef.current?.querySelector('.entry-row-card--selected');
            if (!selected) return;

            const container = getScrollParent(selected);
            if (!container) return;

            const containerTop = container.getBoundingClientRect().top;
            const selectedTop = selected.getBoundingClientRect().top;
            const selectedBottom = selected.getBoundingClientRect().bottom;
            const containerBottom = container.getBoundingClientRect().bottom;

            if (selectedTop < containerTop) {
                container.scrollTop -= containerTop - selectedTop + 8;
            } else if (selectedBottom > containerBottom) {
                container.scrollTop += selectedBottom - containerBottom + 8;
            }
        }, 50);
    }, [selectedEntry]);

    // CHANGED - WASD navigation via useKeyboardNav hook
    useKeyboardNav({
        entries,
        selectedRk: selectedEntry?.rk,
        onSelect,
        meta: { page: 1 },
        totalPages: 1,
        onPageChange: () => {},
        disabled: isDirty,
    });

    if (!entries.length) {
        return (
            <Typography variant="caption" sx={{ color: J.muted, p: 1, display: 'block' }}>
                Select a topic to view entries.
            </Typography>
        );
    }

    entries = _.orderBy(entries, ['updatedOn'], ['desc']);

    return (
        <Box
            ref={listRef}
            id="entry-panel-root"
            sx={{
                bgcolor: 'var(--j-canvas)',
                flex: 1,
                minHeight: 0,
                '&::-webkit-scrollbar': { width: '4px' },
                '&::-webkit-scrollbar-track': { bgcolor: 'transparent' },
                '&::-webkit-scrollbar-thumb': { bgcolor: '#294766', borderRadius: '2px' },
            }}
        >
            {entries.map((entry) => (
                <EntryRow
                    //
                    key={entry.rk}
                    entry={entry}
                    isSelected={selectedEntry?.rk === entry.rk}
                    onSelect={onSelect}
                />
            ))}
        </Box>
    );
}
